import { Box, Stack, Text } from '@ltvco/refresh-lib/theme';
import History from '@mui/icons-material/History';
import OpenInNew from '@mui/icons-material/OpenInNew';

import {
  AI_DISCLAIMER_BODY,
  AI_DISCLAIMER_PREFIX,
  DEFAULT_VISIT_CLAIM_CTA_LABEL,
} from '../shared/constants';
import { ReportStatStrip } from '../shared/ReportStatStrip';
import { BulletList, DefendantHeading } from '../shared/reportTypography';
import { toSafeHttpUrl } from '../shared/safeHttpUrl';

import {
  AiDisclaimer,
  AtAGlanceShell,
  AtAGlanceTitle,
  ClaimCtaButton,
  DateUpdatedRow,
  DateUpdatedText,
  HeroCaseTitle,
  OverviewPanel,
} from './components';

import type { DocketDetailSections } from '../types';

export function ReportOverview({
  header,
  settlement_card,
  at_a_glance,
}: Pick<DocketDetailSections, 'header' | 'settlement_card' | 'at_a_glance'>) {
  const defendant = settlement_card.primary_defendant_or_target?.trim();
  const caseTitle =
    header.case_title?.trim() || settlement_card.case_caption?.trim() || '';
  const claimUrl = toSafeHttpUrl(settlement_card.visit_claim_url);
  const hasClaimUrl = Boolean(claimUrl);
  const claimCtaLabel = DEFAULT_VISIT_CLAIM_CTA_LABEL;

  const showStats =
    Boolean(settlement_card.deadline_absolute_text?.trim()) ||
    Boolean(settlement_card.payout_summary?.trim()) ||
    Boolean(settlement_card.proof_requirement_summary?.trim());

  const showAtAGlance = (at_a_glance.bullets?.length ?? 0) > 0;

  const updatedText = header.last_updated_text?.trim();
  const hasOverviewBody =
    Boolean(updatedText) ||
    Boolean(defendant) ||
    Boolean(caseTitle) ||
    hasClaimUrl ||
    showStats ||
    showAtAGlance;

  if (!hasOverviewBody) {
    return null;
  }

  return (
    <OverviewPanel>
      {updatedText ? (
        <DateUpdatedRow>
          <History
            sx={(theme) => ({
              fontSize: 16,
              color: theme.palette.primary.main,
            })}
            aria-hidden
          />
          <DateUpdatedText>{updatedText}</DateUpdatedText>
        </DateUpdatedRow>
      ) : null}

      <Stack spacing={4}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            justifyContent: 'space-between',
            gap: 4,
          }}
        >
          <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
            {defendant ? (
              <DefendantHeading sx={{ display: 'block' }}>
                {defendant}
              </DefendantHeading>
            ) : null}
            {caseTitle ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: 0.75,
                }}
              >
                <HeroCaseTitle sx={{ display: 'block', flex: 1, minWidth: 0 }}>
                  {caseTitle}
                </HeroCaseTitle>
                {hasClaimUrl ? (
                  <OpenInNew
                    sx={(theme) => ({
                      fontSize: 20,
                      color: theme.palette.primary.main,
                      mt: 0.5,
                      flexShrink: 0,
                    })}
                    aria-hidden
                  />
                ) : null}
              </Box>
            ) : null}
          </Stack>
          {hasClaimUrl ? (
            <ClaimCtaButton
              variant="contained"
              onClick={() =>
                claimUrl &&
                window.open(claimUrl, '_blank', 'noopener,noreferrer')
              }
            >
              {claimCtaLabel}
            </ClaimCtaButton>
          ) : null}
        </Box>

        {showStats ? (
          <ReportStatStrip
            alwaysThreeColumns
            deadlineAbsolute={settlement_card.deadline_absolute_text?.trim()}
            payout={settlement_card.payout_summary?.trim()}
            proof={settlement_card.proof_requirement_summary?.trim()}
          />
        ) : null}

        {showAtAGlance ? (
          <AtAGlanceShell>
            <AtAGlanceTitle sx={{ display: 'block' }}>
              At A Glance
            </AtAGlanceTitle>
            {at_a_glance.bullets?.length ? (
              <BulletList component="ul" sx={{ listStyle: 'disc' }}>
                {at_a_glance.bullets.map((bullet, index) => (
                  <Text component="li" key={`${index}-${bullet}`}>
                    {bullet}
                  </Text>
                ))}
              </BulletList>
            ) : null}
          </AtAGlanceShell>
        ) : null}
      </Stack>

      <AiDisclaimer>
        <Box component="span" sx={{ fontWeight: 700 }}>
          {AI_DISCLAIMER_PREFIX}
        </Box>{' '}
        {AI_DISCLAIMER_BODY}
      </AiDisclaimer>
    </OverviewPanel>
  );
}
